import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import { MainHeader } from '../../components/Header';
import injectSaga from '../../utils/injectSaga';
import injectReducer from '../../utils/injectReducer';
import { MakePayment } from '../../components/MakePayment';
import { getAccountId, getUserName } from '../../localStorage';
import { checkPermissionApplyPayment } from '../../utils/CheckPermissions';
import { makeGetPermissionSelfCare, makeGetAccountInfo } from '../App/selectors';
import { FormHeader } from '../../components/form';
import makeSelectMakePaymentPage from './selectors';
import { GenericInput } from '../../components/common';
import reducer from './reducer';
import saga from './saga';
import { getAccountById, manageAccount, selectInvoiceId, applyPayment, applyExternalPayment } from '../App/actions';
import createPlaceToPaySession from '../../api/payments/createPlaceToPaySession';
import verifyPlaceToPaySession from '../../api/payments/verifyPlaceToPaySession';
import capturePaymentToken from '../../api/payments/capturePaymentToken';
import './styles.scss';

const PLACETOPAY_SESSION_KEY = 'p2p_pending';

// PlaceToPay verification UI state (banner displayed at top of the make-payment page
// when the user returns from PlaceToPay's hosted checkout).
const P2P_UI = {
  IDLE: 'IDLE',
  CHECKING: 'CHECKING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  EXPIRED: 'EXPIRED',
  AMOUNT_MISMATCH: 'AMOUNT_MISMATCH',
  ERROR: 'ERROR',
};

let totalAmount = 0;



const invoiceTableColumns = [
  {
    name: 'id',
    label: 'label.invoiceId',
  },
  {
    name: 'invoiceDate',
    label: 'label.invoiceDate',
  },
  {
    name: 'dueDate',
    label: 'label.dueDate',
  },
  {
    name: 'total',
    label: 'label.invoiceAmount',
  },
  {
    name: 'due',
    label: 'label.due',
  },
];

export class MakePaymentPage extends React.PureComponent {

  invoiceFetchInFlight = false;
invoiceFetchSeq = 0;

  constructor() {
    super();

    this.state = {
      makePayment: {},
      creditCards: [{}],
      invoiceData: [],
      // PlaceToPay return-from-vendor verification state. Hidden until set.
      p2pUiState: P2P_UI.IDLE,
      p2pDetails: {},
      p2pErrorMessage: '',
    };
  }

  componentDidMount() {
    this.doGetAccountDetails();
    const { accountDetails } = this.props;
    if (accountDetails && accountDetails.id) {
      this.doGetSelectInvoiceId();
    }
    // If we're returning from a PlaceToPay redirect, resume the verify+apply flow.
    // The trigger is the sessionStorage key written before redirecting out.
    this.resumePlaceToPayFlowIfPending();
  }

  /**
   * Called from componentDidMount. If sessionStorage has a pending PlaceToPay session
   * (set by onPayWithPlaceToPay before redirecting to the vendor's hosted page), call
   * payment-gateway's /verify to confirm the vendor's outcome, then — if APPROVED —
   * fire the GraphQL applyExternalPayment mutation so engine records the payment.
   *
   * Two layers of single-fire protection:
   *
   * 1. sessionStorage is CLEARED IMMEDIATELY on read, before any async call. A
   *    PlaceToPay session is single-use — if our verify or apply fails for any
   *    reason, we don't want a later componentDidMount (e.g. Redux re-render,
   *    React StrictMode double-mount in dev, or a route remount) to retry the
   *    same session and trigger the engine's idempotency guard with confusing
   *    toast errors. If retry is needed, the user starts a brand-new PlaceToPay
   *    session via the "Pay with PlaceToPay" button.
   *
   * 2. A component-instance flag (this.p2pResumeFired) prevents the verify+apply
   *    chain from firing twice even within the same component lifecycle.
   */
  resumePlaceToPayFlowIfPending = () => {
    if (this.p2pResumeFired) return;

    let pending = null;
    try {
      const raw = sessionStorage.getItem(PLACETOPAY_SESSION_KEY);
      if (raw) pending = JSON.parse(raw);
      // IMPORTANT: clear immediately, before any async call. See JSDoc above.
      if (raw) sessionStorage.removeItem(PLACETOPAY_SESSION_KEY);
    } catch (e) {
      // ignore JSON parse errors, treat as no pending
    }
    if (!pending || !pending.requestId) return;

    this.p2pResumeFired = true;
    this.setState({ p2pUiState: P2P_UI.CHECKING });

    verifyPlaceToPaySession(pending.requestId, {
      amount: pending.amount,
      currency: pending.currency,
    })
      .then((verifyResult) => {
        if (!verifyResult) {
          this.setState({ p2pUiState: P2P_UI.ERROR, p2pErrorMessage: 'No response from verify' });
          return;
        }
        if (verifyResult.successful === true) {
          // Server has confirmed APPROVED + amount matches. Now record on Embrix side.
          // Use the dedicated applyExternalPayment mutation — engine bookkeeps
          // without calling the vendor (PlaceToPay already charged the card on
          // its hosted page). No card data is sent because there isn't any: the
          // payment_credit_cards table stays clean of placeholder rows.
          const externalTxnId = verifyResult.transactionId || String(pending.requestId);
          const externalPayload = {
            accountId: pending.accountId || getAccountId(),
            userId: getUserName(),
            paymentDate: moment().format('YYYY-MM-DD'),
            source: 'AGENT_CARE',
            amount: Number(pending.amount),
            // currency intentionally omitted: engine falls back to account.currency.
            // Sending an explicit currency that differs from account.currency triggers
            // the cross-currency conversion path (PGPaymentsService.postPayment), which
            // requires a configured exchange-rate row — out of scope for the PlaceToPay
            // POC. By omitting it, engine treats this payment as same-currency-as-account.
            allocationData: [
              {
                invoiceUnitId: pending.invoiceUnitId,
                amount: Number(pending.amount),
                accountId: pending.accountId || getAccountId(),
              },
            ],
            vendorName: 'PLACETOPAY',
            vendorTransactionId: externalTxnId,
            vendorAuthorizationCode: verifyResult.authorizationCode,
            vendorReceipt: verifyResult.receipt,
            vendorLast4: verifyResult.last4,
          };
          this.props.applyExternalPayment(externalPayload, (res) => {
            // sessionStorage was already cleared up-front when we read it — no
            // additional cleanup needed here.
            this.setState({
              p2pUiState: P2P_UI.APPROVED,
              p2pDetails: { ...verifyResult, embrixResult: res },
            });
            // Refresh the invoice table to show updated balances.
            this.doGetSelectInvoiceId();
          });
        } else if (verifyResult.status === 'AMOUNT_MISMATCH') {
          // Don't fire applyExternalPayment — recording it would book a wrong amount.
          this.setState({ p2pUiState: P2P_UI.AMOUNT_MISMATCH, p2pDetails: verifyResult });
        } else if (verifyResult.status === 'EXPIRED') {
          this.setState({ p2pUiState: P2P_UI.EXPIRED, p2pDetails: verifyResult });
        } else {
          this.setState({ p2pUiState: P2P_UI.REJECTED, p2pDetails: verifyResult });
        }
      })
      .catch((err) => {
        // Leave sessionStorage alone — a transient network glitch should be retryable
        // on next visit to /make-payment without re-paying.
        // eslint-disable-next-line no-console
        console.error('PlaceToPay verify failed', err);
        this.setState({
          p2pUiState: P2P_UI.ERROR,
          p2pErrorMessage: (err && err.message) || String(err),
        });
      });
  };

  renderPlaceToPayBanner = () => {
    const { p2pUiState, p2pDetails, p2pErrorMessage } = this.state;
    if (p2pUiState === P2P_UI.IDLE) return null;

    const baseStyle = {
      padding: '14px 18px',
      margin: '12px 0',
      borderRadius: 4,
      borderLeft: '4px solid',
      background: '#f7f8fa',
    };
    const map = {
      CHECKING:        { color: '#2547a5', title: 'Verifying your payment…',
                         text: 'Confirming with PlaceToPay. Please don’t close this tab.' },
      APPROVED:        { color: '#2e7d32', title: 'Payment approved',
                         text: `Receipt: ${p2pDetails.receipt || 'n/a'} | Authorization: ${p2pDetails.authorizationCode || 'n/a'} | Amount: ${p2pDetails.amount || ''} ${p2pDetails.currency || ''}` },
      REJECTED:        { color: '#c62828', title: 'Payment was not approved',
                         text: p2pDetails.message || p2pDetails.status || 'unknown' },
      EXPIRED:         { color: '#ef6c00', title: 'Payment session expired',
                         text: 'The session expired before payment was completed. No money was charged. You can try again.' },
      AMOUNT_MISMATCH: { color: '#c62828', title: 'Amount mismatch — NOT recorded',
                         text: `Vendor charged a different amount than we expected. Reference: ${p2pDetails.reference || 'n/a'}. Please contact support before retrying.` },
      ERROR:           { color: '#c62828', title: 'Could not verify payment',
                         text: `${p2pErrorMessage || 'unknown error'}. The PlaceToPay session is still authoritative; if you closed mid-payment, the session expires in <30 min with no charge.` },
    };
    const e = map[p2pUiState];
    if (!e) return null;
    return (
      <div style={{ ...baseStyle, borderLeftColor: e.color }}>
        <h4 style={{ margin: '0 0 6px 0', color: e.color }}>{e.title}</h4>
        <p style={{ margin: 0 }}>{e.text}</p>
      </div>
    );
  };

 doGetSelectInvoiceId = () => {
  const { selectInvoiceId } = this.props;

  if (this.invoiceFetchInFlight) return Promise.resolve([]);
  this.invoiceFetchInFlight = true;

  const accountId = getAccountId();

  const normalizeRows = (result) => {
    if (!result || !result.success) return [];

    const data = result.data;
    if (Array.isArray(data)) return data;

    const root = data && data.searchInvoiceUnits;
    if (Array.isArray(root)) return root;
    if (root && Array.isArray(root.invoiceUnitData)) return root.invoiceUnitData;
    if (root && Array.isArray(root.items)) return root.items;
    if (root && Array.isArray(root.content)) return root.content;

    return [];
  };

  const fetchByStatus = (status) =>
    new Promise((resolve) => {
      try {
        selectInvoiceId(
          {
            page: 1,
            size: 200,
            sort: 'dueDate_DESC',
            filter: { accountId, status },
          },
          (result) => resolve(normalizeRows(result))
        );
      } catch (e) {
        // swallow errors so no global popup
        resolve([]);
      }
    });

  return fetchByStatus('ACTIVE')
    .then((activeRows) =>
      fetchByStatus('COLLECTION').then((collectionRows) => {
        const merged = (activeRows || []).concat(collectionRows || []);
        this.setState({ invoiceData: merged });
        return merged;
      })
    )
    .catch(() => {
      // swallow everything so no popup
      this.setState({ invoiceData: [] });
      return [];
    })
    .finally(() => {
      this.invoiceFetchInFlight = false;
    });
};


  doGetAccountDetails = () => {
    const { accountDetails, getAccountById } = this.props;
    if (!accountDetails || !accountDetails.id || accountDetails.id !== getAccountId()) {
      getAccountById(
        {
          id: getAccountId(),
        },
        ({ success, data }) => {
          if (
            success &&
            data &&
            data.paymentProfiles &&
            data.paymentProfiles[0] &&
            data.paymentProfiles[0].creditCards
          ) {
            this.setState({ creditCards: data.paymentProfiles[0].creditCards });
          }
        }
      );
    } else {
      this.setState({
        creditCards: accountDetails?.paymentProfiles[0]?.creditCards
          ? accountDetails.paymentProfiles[0].creditCards
          : [{}],
      });
    }
  };

  handleAddCreditCardFromURL = payload => {
    this.props.manageAccount(payload, success => {
      if (success) {
        this.props.history.push('/make-payment');
        this.doGetAccountDetails();
      }
    });
  };

  onChangeValueForm = (key, value) => {
    const { makePayment } = this.state;
    makePayment[key] = value;
    this.setState({
      ...makePayment,
    });
  };

  /**
   * Initiate a PlaceToPay (Evertec) WebCheckout session for the selected invoice(s).
   *
   * Differs from onPay() in that NO stored card token is used and engine is NOT
   * called yet — the customer enters card details on PlaceToPay's hosted page.
   * Engine writes ar_ops only after /payment-return verifies the session as APPROVED.
   */
  onPayWithPlaceToPay = () => {
    const { invoiceData } = this.state;
    const selected = (invoiceData || []).filter((inv) => !!inv.isSelcted);
    if (!selected.length) {
      // eslint-disable-next-line no-alert
      alert('Please select at least one invoice to pay.');
      return;
    }
    if (selected.length > 1) {
      // PlaceToPay session is one reference per session — keep POC scope simple.
      // eslint-disable-next-line no-alert
      alert('PlaceToPay flow currently supports one invoice at a time. Please select a single invoice.');
      return;
    }
    const invoice = selected[0];
    const amount = Number(invoice.due) || 0;
    if (!amount || amount <= 0) {
      // eslint-disable-next-line no-alert
      alert('Invoice has no outstanding amount to pay.');
      return;
    }
    const accountId = invoice.accountId || getAccountId();
    const invoiceUnitId = invoice.invoiceUnitId || invoice.id;
    const reference = `${invoiceUnitId}-${Date.now()}`;
    // Currency is sourced from the customer's account (loaded via getAccountById on mount).
    // This is what the vendor charges in AND what engine uses to record the payment.
    // No tenant-specific fallback — if the account has no currency set, the payment
    // would fail validation downstream, which is the correct behavior (force ops to fix
    // the account rather than silently picking a default).
    const { accountDetails } = this.props;
    const currency = accountDetails && accountDetails.currency;
    if (!currency) {
      // eslint-disable-next-line no-alert
      alert(
        'This account has no currency configured. PlaceToPay cannot create a session ' +
        'without one. Please contact support.',
      );
      return;
    }

    // Return to /make-payment (this same page). The componentDidMount picks up
    // the pending session from sessionStorage and runs the verify+applyPayment flow.
    // This is the most stable return URL — the page is already loaded and avoids
    // the cold-route-load failures the standalone /payment-return route was prone to.
    const returnUrl = `${window.location.origin}/make-payment`;

    const body = {
      amount,
      currency,
      reference,
      description: `Embrix invoice ${invoiceUnitId}`,
      returnUrl,
    };

    createPlaceToPaySession(body)
      .then((session) => {
        if (!session || !session.processUrl) {
          throw new Error('payment-gateway returned no processUrl');
        }
        try {
          sessionStorage.setItem(
            PLACETOPAY_SESSION_KEY,
            JSON.stringify({
              requestId: session.requestId,
              invoiceUnitId,
              accountId,
              amount,
              currency,
              reference,
            }),
          );
        } catch (e) {
          // sessionStorage may be unavailable in some private modes; still proceed.
        }
        // Full-page nav. The vendor will redirect back to returnUrl when finished.
        window.location.assign(session.processUrl);
      })
      .catch((err) => {
        // eslint-disable-next-line no-alert, no-console
        console.error('createPlaceToPaySession failed', err);
        // eslint-disable-next-line no-alert
        alert(`Could not start PlaceToPay session: ${err && err.message ? err.message : err}`);
      });
  };

  onClickCheckBox = (e, item) => {
    try {
      const { invoiceData } = this.state;
      const newData = cloneDeep(invoiceData);
      const indexData = newData.findIndex(inv => inv.id === item.id);
      newData[indexData].isSelcted = !item.isSelcted;
      this.setState({ invoiceData: newData });
    } catch (error) {
      console.log(error);
    }
  };

 onPay = () => {
  const debugId = `PAY-${Date.now()}`;
  const { invoiceData, creditCards } = this.state;
  const { applyPayment, applyExternalPayment, accountDetails } = this.props;

  console.group(`[${debugId}] onPay START`);
  console.log('accountId', getAccountId());
  console.log('userId', getUserName());
  console.log('creditCards length', creditCards ? creditCards.length : 0);
  console.log('invoiceData length', invoiceData ? invoiceData.length : 0);

  const selected = (invoiceData || []).filter((inv) => !!inv.isSelcted);
  console.log('selected invoices count', selected.length);
  console.log('selected invoices (raw)', selected);

  const allocationData = selected.map((inv) => {
    const invoiceUnitId = inv.invoiceUnitId || inv.id;
    const due = Number(inv.due) || 0;

    const row = {
      invoiceUnitId,
      amount: due,
      accountId: inv.accountId || getAccountId(),
    };

    console.log('allocation row', { invoiceUnitId, due, row, inv });
    return row;
  });

  const computedAmount = allocationData.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

  if (!allocationData.length) {
    console.warn(`[${debugId}] No invoices selected -> aborting pay`);
    console.groupEnd();
    return;
  }

  if (!computedAmount || computedAmount <= 0) {
    console.warn(`[${debugId}] computedAmount is 0 -> aborting pay`, computedAmount);
    console.groupEnd();
    return;
  }

  // verify we have a CC
  if (!creditCards || !creditCards.length || !creditCards[0].cardToken) {
    alert('Missing saved card token. Please add a card first.');
    return;
  }

  const savedCard = creditCards[0];
  const isPlaceToPay = savedCard.merchant === 'PLACETOPAY';

  // PLACETOPAY TOKEN FLOW
   if (isPlaceToPay) {
     const currency = accountDetails && accountDetails.currency;
     if (!currency) {
       alert('Account currency is missing. Cannot process token charge.');
       return;
     }

     // Set UI to checking state
     this.setState({ p2pUiState: P2P_UI.CHECKING });

     // Build REST payload
     const capturePayload = {
       vendor: "PLACETOPAY",
       operation: "CAPTURE",
       reference: `${allocationData[0].invoiceUnitId}-${Date.now()}`,
       description: `Invoice Payment via Saved Token`,
       amount: computedAmount,
       currency: currency,
       buyerEmail: accountDetails?.email || 'noreply@embrix.com',
       creditCard: {
         vendor: "PLACETOPAY",
         paymentMethodToken: savedCard.cardToken
       }
     };

     // Execute charge
     capturePaymentToken(capturePayload)
       .then((captureResult) => {
         if (captureResult && captureResult.successful) {
           // Payment successful, map to ledge via applyExternalPayment
           const externalPayload = {
             accountId: getAccountId(),
             userId: getUserName(),
             paymentDate: moment().format('YYYY-MM-DD'),
             source: 'AGENT_CARE',
             amount: computedAmount,
             allocationData,
             vendorName: 'PLACETOPAY',
             vendorTransactionId: captureResult.transactionId || captureResult.reference,
             vendorAuthorizationCode: captureResult.authorizationCode,
             vendorReceipt: captureResult.receipt,
             vendorLast4: captureResult.last4 || savedCard.last4CC,
           };

           applyExternalPayment(externalPayload, (res) => {
             this.setState({
               p2pUiState: P2P_UI.APPROVED,
               p2pDetails: { ...captureResult, embrixResult: res },
             });
             this.doGetSelectInvoiceId(); // refresh balances
           });

         } else {
           // Gateway declined the token
           this.setState({ p2pUiState: P2P_UI.REJECTED, p2pDetails: captureResult });
         }
       })
       .catch((err) => {
         console.error('PlaceToPay Token Capture Failed', err);
         this.setState({
           p2pUiState: P2P_UI.ERROR,
           p2pErrorMessage: err.message || String(err),
         });
       });

     return;
   }

  const payload = {
    accountId: getAccountId() || null,
    paymentCreditCards: [{}],
    source: 'AGENT_CARE',
    userId: getUserName() || 'UserX002',
    paymentDate: moment().format('YYYY-MM-DD'),
    method: 'CREDIT_CARD',
    allocationData,
    amount: computedAmount,
  };

  if (creditCards && creditCards.length && creditCards[0]) {
    payload.paymentCreditCards = [
      {
        cardToken: creditCards[0].cardToken,
        cardExpiry: creditCards[0].cardExpiry,
        last4CC: creditCards[0].last4CC,
        merchant: creditCards[0].merchant,
      },
    ];
  }

  console.log('FINAL payload', payload);

  if (!payload.paymentCreditCards || !payload.paymentCreditCards[0] || !payload.paymentCreditCards[0].cardToken) {
    console.warn(`[${debugId}] Missing cardToken -> aborting pay`);
    console.groupEnd();
    return;
  }

  // Snapshot BEFORE payment so we can verify AFTER refresh
  const beforeSnapshot = selected.map((inv) => ({
    invoiceUnitId: inv.invoiceUnitId || inv.id,
    beforeDue: Number(inv.due) || 0,
    beforeStatus: inv.invoiceStatus || inv.status || null,
  }));
  console.log(`[${debugId}] beforeSnapshot`, beforeSnapshot);

  applyPayment(payload, (res) => {
    console.group(`[${debugId}] applyPayment CALLBACK`);
    console.log('raw response', res);

    // IMPORTANT:
    // Do NOT decide success based on paymentId.
    // Backend can return id:null even when payment actually applies.
    // Instead: refresh invoices and verify due/status changed.
    this.doGetSelectInvoiceId()
      .then((rows) => {
        const list = rows || [];
        console.log(`[${debugId}] invoices after refresh length`, list.length);

        const notImproved = beforeSnapshot.filter(({ invoiceUnitId, beforeDue }) => {
          const found = list.find((r) => (r.invoiceUnitId || r.id) === invoiceUnitId);

          // If invoice disappears from list, treat as success (likely closed/paid)
          if (!found) return false;

          const afterDue = Number(found.due) || 0;

          // success if due is now 0 or decreased
          if (afterDue === 0) return false;
          if (beforeDue > 0 && afterDue < beforeDue) return false;

          // otherwise still looks unpaid
          return true;
        });

        console.log(`[${debugId}] notImproved`, notImproved);

        if (notImproved.length > 0) {
          alert('Payment could not be applied. Please try again or contact support.');
        } else {
          console.log(`[${debugId}] Payment appears applied (invoice due/status changed)`);
        }
      })
      .catch((e) => {
        // If refresh fails, don't show misleading "payment failed" popup
        console.warn(`[${debugId}] invoice refresh failed after payment`, e);
      })
      .finally(() => {
        console.groupEnd(); // end CALLBACK group
        console.groupEnd(); // end START group
      });
  });
};


  renderContent() {
    const { invoiceData, creditCards } = this.state;
    const { permissionsSelfCare } = this.props;

console.log('RENDER invoiceData length', this.state.invoiceData && this.state.invoiceData.length);

    let modeApplyPayment = 0;
    if (permissionsSelfCare && permissionsSelfCare.selfCareModulePermissions) {
      const listPermission = permissionsSelfCare.selfCareModulePermissions;
      modeApplyPayment = checkPermissionApplyPayment({ listPermission });
    }

    totalAmount = 0;
    if (invoiceData && invoiceData.length) {
      invoiceData.forEach(i => {
        const { isSelcted, due } = i;
        if (isSelcted && due) totalAmount += due;
      });
    }

    const newInvoiceTableColumns = [
      {
        name: 'action',
        label: 'label.select',
        render: (colName, item) => (
          <GenericInput
            value={!!item.isSelcted}
            name="action"
            type="checkbox"
            onChange={e => {
              this.onClickCheckBox(e, item);
            }}
            wrapperClass="col-md-12"
            readOnly
          />
        ),
      },
      ...invoiceTableColumns,
    ];

    return (
      <div className="payment">
        {this.renderPlaceToPayBanner()}
        <FormHeader
          title="One-Off payment"
          detail={{ name: 'Account Number:', value: getAccountId() }}
          className="large-font-size"
        />
        <FormHeader title="Credit Card Payment" className="large-font-size" />
        <MakePayment
          history={this.props.history}
          data={creditCards}
          modeApplyPayment={modeApplyPayment}
          handleAddCreditCardFromURL={this.handleAddCreditCardFromURL}
          invoiceData={invoiceData}
          invoiceTableColumns={newInvoiceTableColumns}
          // onClickRow={this.onClickCheckBox}
          onPay={this.onPay}
          onPayWithPlaceToPay={this.onPayWithPlaceToPay}
          totalAmount={totalAmount}
        />
      </div>
    );
  }

  render() {
    return (
      <div className="make-payment-page">
        <MainHeader activeTab="MakePayment" />
        <div className="make-payment-page__form">
          <div className="form-payment">{this.renderContent()}</div>
        </div>
      </div>
    );
  }
}

MakePaymentPage.propTypes = {
  history: PropTypes.object,
  manageAccount: PropTypes.func,
  permissionsSelfCare: PropTypes.object,
};

const mapStateToProps = createStructuredSelector({
  makePayment: makeSelectMakePaymentPage(),
  permissionsSelfCare: makeGetPermissionSelfCare() || {},
  accountDetails: makeGetAccountInfo() || {},
});

const withConnect = connect(mapStateToProps, {
  getAccountById,
  manageAccount,
  selectInvoiceId,
  applyPayment,
  applyExternalPayment,
});

const withReducer = injectReducer({ key: 'makePaymentReducer', reducer });
const withSaga = injectSaga({ key: 'makePaymentSaga', saga });

export default compose(withReducer, withSaga, withConnect)(MakePaymentPage);
